import { TourAnchor } from "training/anchors";
import {
  abnApplies,
  offersPdf,
  showsBenefits,
} from "training/engine/conditions";
import { ITourStep } from "training/types";

import { pdfNoteDone } from "./gates";
import { ESTIMATE_ACTIONS_MENU, ESTIMATE_ACTIONS_MENU_FIRST } from "./shared";

/**
 * CLE-3015 Module 3: How to search for and view an estimate. Text is John's (review 1, 2026-10-23) where he
 * gave wording; the rest is the curriculum's (curriculum-v3, 2026-10-21).
 *
 * Review 1 deleted 3.10 (the row's "..." menu, which Module 1 already shows) and the Expired and Action
 * Needed cards (3.11, 3.12); the module ends on the PDF download.
 *
 * The Estimate Details page was redesigned (CLE-2961) after the curriculum was checked:
 * - The page's title is the patient's name with the status chip next to it.
 * - The Patient card's title is "Patient"; "Outreach Enabled" and its three buttons are at the card's foot.
 * - "Patient Benefits" is titled "Insurance & Benefits"; "Service Details" is the table inside "Provider
 *   Calculations".
 * - Duplicate and Download ABN are in the "..." menu; each such step has the menu `opener` (see ./shared).
 *   Download GFE is a header button since CLE-3018.
 *
 * Open-cancel rows (3.4, 3.5, 3.8): the click on the pencil / menu item says what to click; the explanation
 * shows on the Cancel / No step, while the edit mode or dialog is open.
 */
export const MODULE_3: ITourStep[] = [
  {
    id: "m3.01-search",
    module: 3,
    anchor: TourAnchor.SearchBox,
    label: "Search by patient name, payer, or account number",
    mode: "fill",
    route: "estimates",
    // The module's purpose (the popover's intro) carries John's first sentence.
    text: "To start, use this search bar to search your last name to find the Estimate you just created — this is how you find estimates for patients who may call or walk in. You can also search by Account Number or Member ID.",
  },
  {
    id: "m3.02-view",
    module: 3,
    anchor: TourAnchor.ViewFirstRow,
    label: "View",
    mode: "click",
    // View opens /orders/:id itself. If the top row is not the trainee's own estimate, the Estimate
    // Details host sends them to theirs (TrainingTourHost).
    advanceOn: "route",
    route: "estimates",
    // Instruction column.
    text: 'Click "View" on your row.',
  },
  {
    id: "m3.02b-estimate-details",
    module: 3,
    anchor: TourAnchor.EstimateHeader,
    mode: "look",
    route: "estimate",
    // The page loads the estimate after the navigation.
    waitMs: 8000,
    text: [
      "You are now looking at the Estimate Details page. You'll notice a small chip next to your name - this is the Status, which is also tracked on the previous screen. As statuses change, they will be reflected in this chip.",
      "We will spend the next few tasks reviewing the various aspects of the Estimate Details page.",
    ].join("\n"),
  },
  {
    id: "m3.03-outreach",
    module: 3,
    anchor: TourAnchor.PatientOutreach,
    label: "Outreach Enabled",
    mode: "look",
    route: "estimate",
    text: [
      'Outreach references the text messages that we are sending to the patient. You will see a green "Outreach Enabled" which tells you that the patient is currently receiving text messages.',
      "Stop Outreach will immediately cease sending texts.",
      "Resend Notification sends the same text again. This is used mainly if the patient accidentally deletes the text message or can't find it and needs to look at it again.",
      "Send Custom Text is a one-off message you write yourself. This is useful if you, for example, have a conversation with a patient and want to reference something from that conversation when you send them the text.",
      "We come back to these in Module 5.",
    ].join("\n"),
  },
  {
    id: "m3.04-patient-pencil",
    module: 3,
    anchor: TourAnchor.PatientEdit,
    mode: "open-cancel",
    route: "estimate",
    text: "Now let's edit the patient information. You will see this pencil icon throughout the Estimate Details page — anytime you click this, it will open that section for editing. Go ahead and click this button now.",
    followUp: {
      anchor: TourAnchor.PatientEditCancel,
      label: "Cancel",
      text: [
        "Because we clicked the pencil on the Patient section, now you have the ability to edit the Patient Name, Date of Birth, Phone Number and Email.",
        "Important note! Editing and saving a new phone or email sends the estimate again to the new number. Click Cancel for now as we are not making changes.",
      ].join("\n"),
    },
  },
  {
    id: "m3.05-visit-pencil",
    module: 3,
    anchor: TourAnchor.VisitEdit,
    mode: "open-cancel",
    route: "estimate",
    text: "Now let's edit the Date of Service information for the Visit. Click this pencil now.",
    followUp: {
      anchor: TourAnchor.VisitEditCancel,
      label: "Cancel",
      text: [
        'Now we have the ability to edit the Date and time of service as well as the Account / Visit ID. Notice you can also check the "Not scheduled" box as well.',
        "Important note! Changing the date restarts the reminder texts from the new date.",
        "For now, just hit Cancel as we are not editing anything.",
      ].join("\n"),
    },
  },
  {
    id: "m3.06-benefits",
    module: 3,
    anchor: TourAnchor.PatientBenefits,
    label: "Insurance & Benefits",
    mode: "look",
    route: "estimate",
    when: showsBenefits,
    text: "Below the Visit section is the Insurance & Benefits section. This will detail everything from the eligibility pull we did during Estimate creation. So all of this information is stored here.",
  },
  {
    // 3.7 is two highlights (the Service Details table, then Redo), split at a sentence boundary.
    id: "m3.07-service-details",
    module: 3,
    anchor: TourAnchor.ServiceDetails,
    label: "Service Details",
    mode: "look",
    route: "estimate",
    text: "This section will detail the provider and code information that was supplied during the initial estimate creation. You will notice there is no pencil icon to make edits to this section or the insurance section. This is intentional. Any changes to the insurance or service being done will require re-running the estimate.",
  },
  {
    id: "m3.07b-redo-estimate",
    module: 3,
    anchor: TourAnchor.RedoEstimate,
    label: "Redo",
    mode: "look",
    route: "estimate",
    text: "To make changes to the insurance, the codes, or the benefits, you will click Redo, which is located at the top of the page. This will rebuild the whole estimate and text the patient the new one. Do not click it now, simply click Next.",
  },
  {
    // The opener (ESTIMATE_ACTIONS_MENU_FIRST) says what each item of the menu does.
    id: "m3.08-duplicate",
    module: 3,
    anchor: TourAnchor.DuplicateEstimate,
    label: "Duplicate Estimate",
    mode: "open-cancel",
    route: "estimate",
    opener: ESTIMATE_ACTIONS_MENU_FIRST,
    text: "Go ahead and click Duplicate Estimate now.",
    followUp: {
      anchor: TourAnchor.DuplicateNo,
      side: "right",
      label: "No",
      text: [
        "This function is extremely useful if a patient is coming back for a second visit or perhaps for a code bundle you build often. By selecting this option, it will duplicate everything, including patient information, code selections, etc. From here, you can edit accordingly, re-run eligibility, and save/send the estimate.",
        "Important note! If you are using this feature, be sure to change every appropriate patient field before sending.",
        'For purposes of this training, simply click "No" to advance.',
      ].join("\n"),
    },
  },
  {
    // Not curriculum text — the 3.9 branch "Download ABN appears when an ABN applies". Kept before the PDF so the
    // step ids and the walk's order stay stable.
    id: "m3.09a-download-abn",
    module: 3,
    anchor: TourAnchor.DownloadAbn,
    label: "Download ABN",
    mode: "look",
    route: "estimate",
    opener: ESTIMATE_ACTIONS_MENU,
    when: abnApplies,
    optional: true,
    waitMs: 2000,
    text: 'An ABN (Advance Beneficiary Notice) applies to a code on this estimate, so "Download ABN" is here too: the one the patient signed at checkout, or a blank one to print and sign.',
  },
  {
    id: "m3.09-download-pdf",
    module: 3,
    anchor: TourAnchor.DownloadPdf,
    // CLE-3028 — the button says "Download GFE"; John's text says "Download PDF", its old name.
    label: "Download GFE",
    mode: "click",
    route: "estimate",
    when: offersPdf,
    text: 'At the top you will notice two buttons next to "Redo": Collect Payment and Download GFE. Collect Payment will allow you to take payment from a patient. We will walk through how to do this in the next module, so for now, simply click Download GFE.',
  },
  {
    // The "Add Note for PDF" box, on hospitals whose PDF settings ask for a note first. Some (General Hospital
    // on dev) also ask for Estimated Hours, and Save stays disabled until it has a number (NoteToPdfDialog):
    // the gate waits for the note and, where the box is there, the hours.
    id: "m3.09b-pdf-note",
    module: 3,
    anchor: TourAnchor.PdfNote,
    mode: "fill",
    route: "estimate",
    when: offersPdf,
    optional: true,
    waitMs: 2500,
    done: pdfNoteDone,
    doneHint:
      'Type "training" in the note, and 0 in Estimated Hours if it is there, to continue.',
    text: 'Type "training" in the note. If an Estimated Hours box appears, type 0 in it: Save stays greyed out until it has a number. Then click Next.',
  },
  {
    // Not curriculum text. The module's last step (review 1).
    id: "m3.09c-pdf-note-save",
    module: 3,
    anchor: TourAnchor.PdfNoteSave,
    label: "Save",
    mode: "click",
    route: "estimate",
    when: offersPdf,
    optional: true,
    waitMs: 1000,
    text: 'Click "Save" to download it.',
  },
];
